import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

const PUBLIC_PREFIXES = [
  "/", "/auth", "/pricing", "/features", "/contact", "/press", "/enterprise",
  "/our-story", "/mission", "/benchmarks", "/blog", "/supported-cities",
  "/terms", "/affiliates", "/shared", "/briefing", "/unsubscribe", "/demo",
  "/free-preshift-generator",
];

function isAppRoute(pathname: string): boolean {
  const p = pathname.replace(/\/+$/, "") || "/";
  if (p === "/") return false;
  if (p.startsWith("/blog")) return false;
  if (p.startsWith("/briefing/")) return false;
  if (p.startsWith("/auth")) return false;
  return !PUBLIC_PREFIXES.includes(p);
}

let installed = false;
function installInterceptor(onSave: () => void) {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Intercept fetch — flag successful supabase REST mutations
  const origFetch = window.fetch.bind(window);
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const res = await origFetch(...args);
    try {
      const req = args[0];
      const init = args[1];
      const url = typeof req === "string" ? req : req instanceof URL ? req.toString() : (req as Request).url;
      const method = (init?.method || (req instanceof Request ? req.method : "GET")).toUpperCase();
      if (
        res.ok &&
        url.includes("/rest/v1/") &&
        ["POST", "PATCH", "PUT", "DELETE"].includes(method)
      ) {
        onSave();
      }
    } catch { /* ignore */ }
    return res;
  };

  // Intercept localStorage.setItem — many pages persist there
  try {
    const proto = Storage.prototype;
    const origSet = proto.setItem;
    proto.setItem = function(key: string, value: string) {
      const r = origSet.call(this, key, value);
      // Skip noisy framework keys
      if (!/^(loglevel|sb-|supabase\.auth|i18n)/.test(key)) onSave();
      return r;
    };
  } catch { /* ignore */ }
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function AutoSaveIndicator() {
  const { pathname } = useLocation();
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const onSave = () => {
      setLastSaved(new Date());
      setPulse(true);
      window.setTimeout(() => setPulse(false), 600);
    };
    installInterceptor(onSave);
    const handler = () => onSave();
    window.addEventListener("oplif:autosave", handler);
    return () => window.removeEventListener("oplif:autosave", handler);
  }, []);

  // Tick every 30s so "x min ago" stays fresh if we extend later
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 30000);
    return () => window.clearInterval(id);
  }, []);

  if (!isAppRoute(pathname)) return null;

  const label = lastSaved ? `Saved · ${formatTime(lastSaved)}` : "All changes saved";

  return (
    <div
      className="print-hide fixed top-3 right-3 z-[60] pointer-events-none"
      aria-live="polite"
    >
      <div
        className={cn(
          "flex items-center gap-1.5 rounded-full border border-border bg-card/90 backdrop-blur",
          "px-2.5 py-1 text-xs text-muted-foreground shadow-sm",
          "transition-all",
          pulse && "ring-2 ring-primary/30",
        )}
      >
        <Check className="w-3.5 h-3.5 text-primary" />
        <span className="font-medium text-foreground">{label}</span>
      </div>
    </div>
  );
}

// Optional helper for components to manually announce a save.
export function markSaved() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("oplif:autosave"));
  }
}
